import React, { useCallback, useEffect, useState } from "react";
import { FormTextInputAutoSuggest } from "../../../components/common";
import { GPS, Location } from "../../../components/common/Icons";
import { usePlacesAutocomplete } from "../../../hooks/usePlacesAutocomplete";

const NO_RESULTS_TEXT = "No results found";
export interface FormTextInputAutoSuggestProps {
	className: string;
	userLocation?: DogsTrust.Place;
	name: string;
	placeholder: string;
	onConfirmUserLocation: (userLocation: DogsTrust.Place) => void;
	onClearUserLocation: () => void;
	noIcons?: boolean;
	placeTypes?: string[];
}

export const LocationInput: React.FC<FormTextInputAutoSuggestProps> = ({
	className,
	name,
	placeholder,
	onConfirmUserLocation,
	onClearUserLocation,
	noIcons = false,
	userLocation,
}) => {
	const [addressSuggestions, setAddressSuggestions] = useState([]);

	const [userLocationInput, setUserLocationInput] = useState(
		userLocation?.address || "",
	);
	const { placePredictions, getPlacePredictions } = usePlacesAutocomplete(100);

	useEffect(() => {
		if (!userLocation) {
			setUserLocationInput("");
		} else setUserLocationInput(userLocation.address);
	}, [userLocation]);
	const onLocationChange = useCallback(
		async (newValue: string) => {
			if (newValue.length < 2) {
				setAddressSuggestions([]);
			}
			if (newValue === userLocationInput) return;
			if (newValue.length >= 2) {
				getPlacePredictions(newValue);
			} else if (userLocationInput?.length > 0 && newValue.length === 0) {
				onClearUserLocation();
			}
			return setUserLocationInput(newValue);
		},
		[getPlacePredictions, onClearUserLocation, userLocationInput],
	);

	const onLocationConfirm = useCallback(
		async (confirmedLocation: string) => {
			if (confirmedLocation === NO_RESULTS_TEXT) {
				return;
			}
			const location = placePredictions.find(
				(place) => place.address === confirmedLocation,
			);
			onConfirmUserLocation(location);
			setUserLocationInput(confirmedLocation);
			setAddressSuggestions([]);
		},
		[onConfirmUserLocation, placePredictions],
	);

	const handleGetUserLocation = async () => {
		navigator.geolocation.getCurrentPosition(async (position) => {
			const { latitude, longitude } = position.coords;
			const geoQuery = await fetch(
				`${process.env.GATSBY_AWS_PLACES_ENDPOINT}postcodes?lon=${longitude}&lat=${latitude}`,
			).then((res) => res.json());
			if (geoQuery.result[0]) {
				setUserLocationInput(geoQuery.result[0].postcode);
				setAddressSuggestions([]);

				onConfirmUserLocation({
					address: geoQuery.result[0].postcode,
					latitude: geoQuery.result[0].latitude,
					longitude: geoQuery.result[0].longitude,
				});
			}
		});
	};

	const placePredictionsString = placePredictions
		.map((place) => place.address)
		.join("");

	// biome-ignore lint/correctness/useExhaustiveDependencies: This is to get the correct behaviour
	useEffect(() => {
		if (placePredictions.length)
			setAddressSuggestions(placePredictions.map((place) => place.address));
		else if (userLocationInput?.length >= 2) {
			setAddressSuggestions([NO_RESULTS_TEXT]);
		} else setAddressSuggestions([]);
	}, [placePredictionsString]);
	return (
		<FormTextInputAutoSuggest
			className={className}
			name={name}
			placeholder={placeholder}
			suggestions={[...addressSuggestions]}
			inputValue={userLocationInput}
			IconLeft={Location}
			IconRight={GPS}
			onIconRightCallback={handleGetUserLocation}
			onChangeCallback={onLocationChange}
			onConfirmCallback={onLocationConfirm}
			noIcons={noIcons}
			ariaIconLeftHidden={true}
			ariaIconRightLabel="Use current location"
		/>
	);
};

export default LocationInput;
